/**
 * PosBackgroundServices — headless React glue that boots the two POS
 * singletons (autoPrintController + newOrderPoller) and attaches the
 * WebView lifecycle listeners that keep them alive on a real POS.
 *
 * KEY DESIGN CHOICE — this component is deliberately lifecycle-independent:
 *
 *   • On mount, it calls start() on both singletons (idempotent).
 *   • It does NOT call stop() on unmount. This is intentional. Under
 *     StrictMode / route re-renders / auth-context re-hydration, React
 *     can unmount and re-mount transiently — if we stopped the loop on
 *     every unmount, the POS would silently lose polling for that gap.
 *     Explicit `logout()` in AuthContext is the ONLY caller that stops
 *     the loops (see PosLayout.handleLogout).
 *
 * Listeners installed here (removed only on module teardown, not on
 * component unmount):
 *   - document.visibilitychange → poke() both loops when app comes back
 *   - window.focus + pageshow    → same
 *   - window.online              → same
 *
 * These are the standard signals Chromium fires when a backgrounded
 * WebView is brought back to the foreground / gets network again. The
 * user's #6 requirement.
 */
import { useEffect, useRef } from 'react';
import autoPrintController from '../../services/printer/autoPrintController';
import newOrderPoller from '../../services/pos/newOrderPoller';
import * as diag from '../../services/pos/posDiagnostics';
import { unlock as unlockAudio } from '../../services/pos/posAudio';

// Module-level flag so listeners are attached only once even across
// remounts of the React component.
let lifecycleListenersAttached = false;

const attachLifecycleListenersOnce = () => {
  if (lifecycleListenersAttached) return;
  lifecycleListenersAttached = true;

  const poke = (reason) => {
    // eslint-disable-next-line no-console
    console.log('[POS_BG] App resumed —', reason, '— poking pollers');
    diag.recordResume(reason);
    try { autoPrintController.poke(); } catch (_e) { /* isolated */ }
    try { newOrderPoller.poke(); } catch (_e) { /* isolated */ }
  };

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) poke('visibilitychange');
  });
  window.addEventListener('focus', () => poke('focus'));
  window.addEventListener('pageshow', () => poke('pageshow'));
  window.addEventListener('online', () => poke('online'));

  // Also: any user interaction inside the POS shell is a valid moment
  // to unlock the AudioContext (a repeat unlock is a no-op).
  const gestureUnlock = () => unlockAudio();
  window.addEventListener('click', gestureUnlock, { passive: true });
  window.addEventListener('touchstart', gestureUnlock, { passive: true });
  window.addEventListener('keydown', gestureUnlock);
};

export default function PosBackgroundServices() {
  const mountLoggedRef = useRef(false);

  useEffect(() => {
    if (!mountLoggedRef.current) {
      // eslint-disable-next-line no-console
      console.log('[POS_BG] MOUNTED');
      // eslint-disable-next-line no-console
      console.log('[POS_BG] Starting services');
      mountLoggedRef.current = true;
    }
    diag.setBgMounted(true);

    // Bridge presence is a synchronous check — record it upfront and on
    // every visibility change (the WebView could have JS-injected the
    // bridge lazily on first pageshow).
    const bridgeAvail = typeof window !== 'undefined' && !!window.AndroidPrinter;
    diag.setBridgeAvailable(bridgeAvail);
    // eslint-disable-next-line no-console
    console.log(`[POS_BG] AndroidPrinter available: ${bridgeAvail}`);

    // Idempotent — safe under StrictMode double-invoke.
    autoPrintController.start();
    // eslint-disable-next-line no-console
    console.log('[POS_BG] AutoPrint controller started');
    newOrderPoller.start();
    // eslint-disable-next-line no-console
    console.log('[POS_BG] Order alert polling started');

    // Try to unlock audio right away — if the user just typed the PIN
    // the context is already resumed via PosLoginPage; if they're
    // navigating around, the click listener handles it.
    unlockAudio();

    attachLifecycleListenersOnce();

    return () => {
      // NOTE: intentionally NOT calling stop() on unmount. See file header.
      diag.setBgMounted(false);
      // eslint-disable-next-line no-console
      console.log('[POS_BG] UNMOUNTED (services still running in background)');
    };
  }, []);

  return null;
}
